import { Box } from '@mui/material';
import { SidebarLink, Tooltip } from '@side/pantry';
import { useAtom, useAtomValue } from 'jotai';
import NextLink from 'next/link';
import {
  type MouseEvent,
  type MouseEventHandler,
  type ReactNode,
  useEffect,
  useRef,
} from 'react';
import { useURLHelpers } from 'src/common/hooks/useURLHelpers';
import {
  CoverSheetLinkBadge,
  type Props as CoverSheetLinkBadgeProps,
} from 'src/components/SideBar/components/CoverSheetLinkBadge';
import { LINK_LABELS, type NavBarPath } from 'src/constants';
import {
  activeInteractionSectionState,
  sideBarLinkBadgeCountState,
} from 'src/state/navigation';
import { getSideBarLinkId } from 'src/utils/navigation';
import { useTrackIfLeavingSendNowPage } from '@/views/Documents/hooks/useSendNow/useTrackIfLeavingSendNowPage';
import { useInteractionLockedContext } from '@/views/Interactions/context/InteractionLockedContext';
import {
  TrackNavigationEvent,
  useTrackNavigationAnalytics,
} from '$analytics/useTrackAnalytics/domains/navigation';

export type LinkProps = NavBarPath & {
  interactionId?: string;
  onClick?: MouseEventHandler<HTMLElement>;
  collapsed?: boolean;
  parentLink?: LinkProps;
  /** @deprecated Use CoverSheetLinkBadgeProps.tooltip */
  badgeTooltip?: string;
  disabled?: boolean;
  /** Tooltip shown over the disabled link explaining why it is unavailable. */
  disabledTooltip?: string;
  /** Analytics eventContainer for the disabled-link tooltip hover event. */
  disabledTooltipEventContainer?: string;
  /** @deprecated Use CoverSheetLinkBadgeProps.formPath */
  formPath?: string;
  CoverSheetLinkBadgeProps?: CoverSheetLinkBadgeProps;
};

export const Link = ({
  interactionId,
  label,
  collapsed,
  path,
  onClick,
  parentLink,
  section,
  badgeTooltip,
  disabled,
  disabledTooltip,
  disabledTooltipEventContainer,
  formPath,
  CoverSheetLinkBadgeProps,
}: LinkProps) => {
  const trackIfLeavingSendNowPage = useTrackIfLeavingSendNowPage();
  const { isLocked } = useInteractionLockedContext();
  const [activeInteractionSection, setActiveInteractionSection] = useAtom(
    activeInteractionSectionState(interactionId),
  );
  const lastActiveInteractionSection = useRef(activeInteractionSection);

  useEffect(() => {
    if (typeof activeInteractionSection !== 'undefined') {
      lastActiveInteractionSection.current = activeInteractionSection;
    }
  }, [activeInteractionSection]);

  const { isCurrentUrl, currentUrl, basePath } = useURLHelpers(path);
  const activeSection =
    activeInteractionSection?.section ??
    lastActiveInteractionSection.current?.section;
  const selected = section
    ? isCurrentUrl && section === activeSection
    : isCurrentUrl;
  const active =
    (!!section && section === activeInteractionSection?.section) || selected;

  const sideBarLinkId = getSideBarLinkId(`${interactionId}`, [
    parentLink?.label,
    label,
  ]);
  const badgeCountState = useAtomValue(
    sideBarLinkBadgeCountState(sideBarLinkId),
  );

  const trackEvent = useTrackNavigationAnalytics();

  const onButtonClick = (event: MouseEvent<HTMLElement>) => {
    trackIfLeavingSendNowPage();

    if (section) {
      setActiveInteractionSection({ section, isScrollingToViaSidebar: true });

      if (parentLink?.label === LINK_LABELS.COVER_SHEET) {
        trackEvent(TrackNavigationEvent.ClickScrollToCoverSheetSubsection, {
          eventTarget: `${section}_coversheet_subsection`,
        });
      }
    } else {
      trackEvent(TrackNavigationEvent.ClickNavigateToSideBarSection, {
        eventTarget: `${event.currentTarget.innerText.replaceAll(
          /\s/g,
          '',
        )}_button`,
      });
    }

    if (onClick) onClick(event);
  };

  const coverSheetBadgeProps = {
    label,
    count: badgeCountState,
    collapsed,
    tooltip: badgeTooltip,
    formPath,
    ...CoverSheetLinkBadgeProps,
  };
  const badgeCount = coverSheetBadgeProps.count;
  const isSubLink = !!parentLink;
  const displayRequiredFieldsBadge =
    !isLocked && !!badgeCount && (!!collapsed || collapsed === undefined);
  const isExternalLink = !path.startsWith(basePath);

  let rightContent: ReactNode = null;
  if (displayRequiredFieldsBadge) {
    rightContent = (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'end',
          marginLeft: 1,
        }}
      >
        <CoverSheetLinkBadge {...coverSheetBadgeProps} />
      </Box>
    );
  }

  return (
    <Tooltip
      title={disabled ? disabledTooltip : undefined}
      // The only disabled sidebar link that renders a tooltip is the
      // unrepresented-client one, so any open here is that event. The container
      // distinguishes which link (closing_timeline vs commission_form).
      onOpen={() => {
        trackEvent(TrackNavigationEvent.UnrepresentedClientTooltipHover, {
          eventContainer: disabledTooltipEventContainer,
          interactionId,
        });
      }}
      placement="top-start"
      slotProps={{
        popper: {
          modifiers: [{ name: 'offset', options: { offset: [0, -20] } }],
        },
      }}
    >
      <SidebarLink
        active={
          label !== LINK_LABELS.COVER_SHEET ? active : collapsed && active
        }
        label={label}
        pathname={path}
        basePath={basePath}
        currentPath={currentUrl}
        collapsed={collapsed}
        disabled={disabled}
        onClick={onButtonClick}
        isSubLink={isSubLink}
        linkComponent={isExternalLink ? undefined : NextLink}
        rightContent={rightContent}
      />
    </Tooltip>
  );
};
